import { get, post, patch } from '@aiptyg/api-client';
import type { TaskDTO, DownloadAppInfoDTO } from '../tasks';
export interface Agent {
  promotion_require_review?: boolean;
  id: number;
  version: number;
  name: string;
  contact_name: string;
  contact_phone: string;
  notes: string;
  allowed_task_types: string[];
  advertising_enabled: boolean;
  market_id: string;
  login_disabled: boolean;
  publish_restricted: boolean;
  business_frozen: boolean;
  created_at: string;
  updated_at: string;
}
export interface AgentAccount {
  id: number;
  username: string;
  display_name: string;
  disabled: boolean;
  last_login_at?: string;
}
export interface AgentTask {
  asset_urls?: Record<string, string>;
  execution_status?: string;
  execution?: {completed_count: number; remaining_count: number; in_flight_count: number; pending_review_count: number; reward_credited_count: number; reward_pending_count: number; max_assignments: number; settlement_scope: "member_task_reward"};
  id: number;
  agent_id: number;
  agent_name?: string;
  version: number;
  current_revision_id: number;
  formal_task_id?: number;
  status: string;
  payload: Partial<TaskDTO> & {
    reward_amount_minor: number;
    download_app?: DownloadAppInfoDTO;
    message_config?: {
      import_id?: number;
      import_count?: number;
      campaign_type: string;
      authorization_reference: string;
      manual_approval_required: boolean;
    };
    completion_screenshot_count?: number;
    submission_requirements?: string;
    remarks?: string;
    required_screenshots?: {
      key: string;
      required: boolean;
      title?: string;
      description?: string;
    }[];
  };
  created_at: string;
  updated_at: string;
}
export interface Revision {
  id: number;
  revision_no: number;
  payload: AgentTask['payload'];
  change_note: string;
  created_at: string;
}
export interface Audit {
  agent_name?: string;
  id: number;
  agent_id?: number;
  portal_task_id?: number;
  task_id?: number;
  actor_kind: string;
  actor_id?: number;
  action: string;
  ip: string;
  metadata: Record<string, unknown>;
  created_at: string;
}
export interface Page<T> {
  execution_counts?: Record<string, number>;
  items: T[];
  total: number;
  page: number;
  page_size: number;
}
export const base = '/v1/admin';
export function query(params: Record<string, string | number | undefined>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') q.set(k, String(v));
  });
  return q.toString();
}
export const listAgents = (params: Record<string, string | number>) =>
  get<Page<Agent>>(`${base}/agents?${query(params)}`);
export const getAgent = (id: number) => get<Agent>(`${base}/agents/${id}`);
export const saveAgent = (
  id: number | undefined,
  data: Record<string, unknown>,
) => {
  if (!id) return post<Agent>(`${base}/agents`, data);
  const {
    username: _username,
    password: _password,
    display_name: _displayName,
    ...profile
  } = data;
  return patch<Agent>(`${base}/agents/${id}`, profile);
};
export const setAgentStatus = (a: Agent, data: Record<string, unknown>) =>
  patch<Agent>(`${base}/agents/${a.id}/status`, {
    ...data,
    expected_version: a.version,
  });
export const accounts = (id: number) =>
  get<{ items: AgentAccount[] }>(`${base}/agents/${id}/accounts`);

export const resetPassword = (
  id: number,
  password: string,
) => post<void>(`${base}/agents/${id}/password-reset`, { password });
export const listAgentTasks = (params: Record<string, string | number>) =>
  get<Page<AgentTask>>(
    `${base}/${params.review ? 'agent-task-reviews' : 'agent-tasks'}?${query(params)}`,
  );
export const getAgentTask = (id: number) =>
  get<AgentTask>(`${base}/agent-tasks/${id}`);
export const taskRevisions = (id: number) =>
  get<{ items: Revision[] }>(`${base}/agent-tasks/${id}/revisions`);
export const taskReviews = (id: number) =>
  get<{ items: Record<string, unknown>[] }>(
    `${base}/agent-tasks/${id}/reviews`,
  );
export const reviewTask = (
  t: AgentTask,
  decision: string,
  note: string,
  internal_note: string,
  key: string,
) =>
  post<unknown>(
    `${base}/agent-task-reviews/${t.id}/${decision === 'approved' ? 'approve' : 'reject'}`,
    {
      decision,
      note,
      internal_note,
      revision_id: t.current_revision_id,
      expected_version: t.version,
      idempotency_key: key,
    },
  );
export const controlTask = (t: AgentTask, status: string, reason: string) =>
  post<AgentTask>(`${base}/agent-tasks/${t.id}/control`, {
    status,
    reason,
    expected_version: t.version,
  });
export const listAudit = (params: Record<string, string | number>) =>
  get<Page<Audit>>(`${base}/agent-audit?${query(params)}`);
export const presentation = (
  id: number,
  sort_order: number,
  is_recommended: boolean,
) =>
  patch<TaskDTO>(`/v1/admin/tasks/${id}/presentation`, {
    sort_order,
    is_recommended,
  });

export const getAgentMessageImport = (id: number) => get<{ filename: string; valid_rows: number; rows: {row_no: number; recipient_type: "phone" | "email"; recipient: string; body: string}[] }>(`${base}/agent-tasks/${id}/message-import`);
